import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@nexus/ui";
import { formatRawMetric, rawMetricKey, type ChartDatum, type ResolvedMetric } from "../chartModel.js";
import { formatTopicTitle } from "../chartPrimitives.js";

interface TableViewProps {
  data: ChartDatum[];
  metrics: ResolvedMetric[];
  currencySymbol: string;
  onTopicSelect?: (topicTitle: string) => void;
}

/** Concept (Topic_Title) × metrics data table. Numeric columns right-aligned with
 *  tabular-nums (DESIGN.md §Tables — styling comes from the @nexus/ui Table primitives). */
export function TableView({ data, metrics, currencySymbol, onTopicSelect }: TableViewProps) {
  return (
    <div className="h-full w-full overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Concept</TableHead>
            {metrics.map((m) => (
              <TableHead key={m.id} className="text-right">
                <span className="inline-flex items-center justify-end gap-1.5">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ background: m.colorVar }}
                    aria-hidden
                  />
                  {m.label}
                </span>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row) => (
            <TableRow
              key={row.category}
              className={onTopicSelect ? "cursor-pointer hover:bg-muted/40" : undefined}
              onClick={onTopicSelect ? () => onTopicSelect(row.category) : undefined}
            >
              <TableCell className="font-medium">{formatTopicTitle(row.category)}</TableCell>
              {metrics.map((m) => {
                const v = row[rawMetricKey(m.id)];
                return (
                  <TableCell key={m.id} className="text-right tabular-nums">
                    {formatRawMetric(m, typeof v === "number" ? v : null, currencySymbol)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
